'use client'

import { type PayloadAction, createSlice } from '@reduxjs/toolkit'

import type {
  Card,
  CardHistory,
  CardStatistics,
  CardType,
  PrintBatch,
} from '@/services/cards.service'
import type { RootState } from '@/store'

interface CardsState {
  // Data
  cards: Card[]
  selectedCard: Card | null
  cardTypes: CardType[]
  statistics: CardStatistics | null
  pendingApprovals: Card[]
  printQueue: Card[]
  printBatches: PrintBatch[]
  cardHistory: CardHistory[]

  // UI State
  isLoading: boolean
  isSaving: boolean
  error: string | null

  // Filters
  filters: {
    search: string
    cardType: string
    status: string
    approvalStatus: string
    branch: string
    hasPhysicalCard: boolean | null
    dateRange: {
      start: string | null
      end: string | null
    }
  }

  // Pagination
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }

  // Modal States
  modals: {
    createCard: boolean
    viewCard: boolean
    approveCard: boolean
    printBatch: boolean
    qrCode: boolean
  }
}

const initialState: CardsState = {
  cards: [],
  selectedCard: null,
  cardTypes: [],
  statistics: null,
  pendingApprovals: [],
  printQueue: [],
  printBatches: [],
  cardHistory: [],
  isLoading: false,
  isSaving: false,
  error: null,
  filters: {
    search: '',
    cardType: '',
    status: '',
    approvalStatus: '',
    branch: '',
    hasPhysicalCard: null,
    dateRange: {
      start: null,
      end: null,
    },
  },
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  modals: {
    createCard: false,
    viewCard: false,
    approveCard: false,
    printBatch: false,
    qrCode: false,
  },
}

const cardsSlice = createSlice({
  name: 'cards',
  initialState,
  reducers: {
    // Data Actions
    setCards: (state, action: PayloadAction<{ cards: Card[]; total: number }>) => {
      state.cards = action.payload.cards
      state.pagination.total = action.payload.total
      state.pagination.totalPages = Math.ceil(action.payload.total / state.pagination.limit)
      state.error = null
    },

    setSelectedCard: (state, action: PayloadAction<Card | null>) => {
      state.selectedCard = action.payload
    },

    setCardTypes: (state, action: PayloadAction<CardType[]>) => {
      state.cardTypes = action.payload
    },

    setStatistics: (state, action: PayloadAction<CardStatistics>) => {
      state.statistics = action.payload
    },

    setPendingApprovals: (state, action: PayloadAction<Card[]>) => {
      state.pendingApprovals = action.payload
    },

    setPrintQueue: (state, action: PayloadAction<Card[]>) => {
      state.printQueue = action.payload
    },

    setPrintBatches: (state, action: PayloadAction<PrintBatch[]>) => {
      state.printBatches = action.payload
    },

    setCardHistory: (state, action: PayloadAction<CardHistory[]>) => {
      state.cardHistory = action.payload
    },

    // Card Operations
    updateCard: (state, action: PayloadAction<Card>) => {
      const index = state.cards.findIndex((card) => card.id === action.payload.id)
      if (index !== -1) {
        state.cards[index] = action.payload
      }
      if (state.selectedCard?.id === action.payload.id) {
        state.selectedCard = action.payload
      }
    },

    removeCard: (state, action: PayloadAction<string>) => {
      state.cards = state.cards.filter((card) => card.id !== action.payload)
      if (state.selectedCard?.id === action.payload) {
        state.selectedCard = null
      }
    },

    addCard: (state, action: PayloadAction<Card>) => {
      state.cards.unshift(action.payload)
      state.pagination.total += 1
      state.pagination.totalPages = Math.ceil(state.pagination.total / state.pagination.limit)
    },

    // Filter Actions
    setFilters: (state, action: PayloadAction<Partial<CardsState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload }
      state.pagination.page = 1 // Reset to first page when filters change
    },

    clearFilters: (state) => {
      state.filters = initialState.filters
      state.pagination.page = 1
    },

    setSearch: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload
      state.pagination.page = 1
    },

    // Pagination Actions
    setPagination: (state, action: PayloadAction<Partial<CardsState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },

    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload
    },

    setLimit: (state, action: PayloadAction<number>) => {
      state.pagination.limit = action.payload
      state.pagination.page = 1 // Reset to first page when limit changes
    },

    // UI State Actions
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },

    setSaving: (state, action: PayloadAction<boolean>) => {
      state.isSaving = action.payload
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },

    clearError: (state) => {
      state.error = null
    },

    // Modal Actions
    setModal: (
      state,
      action: PayloadAction<{ modal: keyof CardsState['modals']; open: boolean }>
    ) => {
      state.modals[action.payload.modal] = action.payload.open
    },

    openCreateModal: (state) => {
      state.modals.createCard = true
      state.selectedCard = null
    },

    openViewModal: (state, action: PayloadAction<Card>) => {
      state.selectedCard = action.payload
      state.modals.viewCard = true
    },

    openApproveModal: (state, action: PayloadAction<Card>) => {
      state.selectedCard = action.payload
      state.modals.approveCard = true
    },

    openPrintBatchModal: (state) => {
      state.modals.printBatch = true
    },

    openQRCodeModal: (state, action: PayloadAction<Card>) => {
      state.selectedCard = action.payload
      state.modals.qrCode = true
    },

    closeAllModals: (state) => {
      Object.keys(state.modals).forEach((key) => {
        state.modals[key as keyof CardsState['modals']] = false
      })
    },

    // Bulk Actions
    selectCards: (state, action: PayloadAction<string[]>) => {
      // Add selected property to cards for bulk operations
      state.cards.forEach((card) => {
        ;(card as any).selected = action.payload.includes(card.id)
      })
    },

    clearSelection: (state) => {
      state.cards.forEach((card) => {
        delete (card as any).selected
      })
    },

    // Reset State
    resetCardsState: () => initialState,
  },
})

// Export actions
export const {
  setCards,
  setSelectedCard,
  setCardTypes,
  setStatistics,
  setPendingApprovals,
  setPrintQueue,
  setPrintBatches,
  setCardHistory,
  updateCard,
  removeCard,
  addCard,
  setFilters,
  clearFilters,
  setSearch,
  setPagination,
  setPage,
  setLimit,
  setLoading,
  setSaving,
  setError,
  clearError,
  setModal,
  openCreateModal,
  openViewModal,
  openApproveModal,
  openPrintBatchModal,
  openQRCodeModal,
  closeAllModals,
  selectCards,
  clearSelection,
  resetCardsState,
} = cardsSlice.actions

// Selectors
export const selectCardsState = (state: RootState) => state.cards
export const selectAllCards = (state: RootState) => state.cards.cards
export const selectSelectedCard = (state: RootState) => state.cards.selectedCard
export const selectCardTypes = (state: RootState) => state.cards.cardTypes
export const selectCardsStatistics = (state: RootState) => state.cards.statistics
export const selectPendingApprovals = (state: RootState) => state.cards.pendingApprovals
export const selectPrintQueue = (state: RootState) => state.cards.printQueue
export const selectCardsFilters = (state: RootState) => state.cards.filters
export const selectCardsPagination = (state: RootState) => state.cards.pagination
export const selectCardsLoading = (state: RootState) => state.cards.isLoading
export const selectCardsError = (state: RootState) => state.cards.error
export const selectCardsModals = (state: RootState) => state.cards.modals

// Export reducer
export default cardsSlice.reducer
